import {
    Link,
    LinkBackground,
    LinkType,
    Theme,
    ThemeSection,
} from '@easeltv/client-sdk';

import { createLabel, getLinkHref } from './LinkAtom.helpers';
import {
    StyledButtonLink,
    StyledExternalLink,
    StyledInternalLink,
} from './LinkAtom.styled';

/* ---------------------------------- Types --------------------------------- */

/** @interface `ThemeSectionT`
 * #### Type for the theme section types
 */
export type ThemeSectionT = keyof Pick<
    Theme['sections'],
    'header' | 'menu' | 'page' | 'footer' | 'sideMenu'
>;

/** @interface `DumbLinkAtomI`
 * #### Interface for the DumbLinkAtom's props object
 */
export interface DumbLinkAtomI {
    /** Optional callback, executed before the user is taken to link destination */
    callback?: () => void;

    /** Whether the link is a call to action */
    cta?: boolean;

    /** Object that makes up a link */
    data: Link;

    /** Usually a four letter string, e.g. 'prod' */
    env: string;

    /** Callback to log the user out */
    logoutCallback: () => void;

    /** Section of the Theme object from SDK */
    section: ThemeSection;

    /** Usually a three letter string, e.g. 'etv' */
    tenant: string;
}

/* -------------------------------- Component ------------------------------- */

const DumbLinkAtom = ({
    callback,
    cta = false,
    data,
    env,
    logoutCallback,
    section,
    tenant,
}: DumbLinkAtomI) => {
    const {
        display,
        background,
        name,
        lightImageId,
        darkImageId,
        openNewTab,
        type,
    } = data;
    const { luminance } = section;
    const target = openNewTab ? '_blank' : '_self';
    const hasBackground = background === LinkBackground.Feature;
    const linkLabel = createLabel({
        background,
        darkImageId,
        display,
        env,
        tenant,
        text: name,
        lightImageId,
        luminance,
    });

    const executeCallBack = () => {
        if (callback) callback();
    };

    if (type === LinkType.External)
        return (
            <StyledExternalLink
                $cta={cta}
                target={target}
                rel="noreferrer"
                $hasBackground={hasBackground}
                href={getLinkHref(data)}
                section={section}
                onClick={executeCallBack}
            >
                {linkLabel}
            </StyledExternalLink>
        );

    if (type === LinkType.System && data.systemPage === 'logout') {
        return (
            <StyledButtonLink
                $cta={cta}
                $hasBackground={hasBackground}
                section={section}
                onClick={logoutCallback}
            >
                {linkLabel}
            </StyledButtonLink>
        );
    }

    return (
        <StyledInternalLink
            $cta={cta}
            to={getLinkHref(data)}
            target={target}
            rel="noreferrer"
            $hasBackground={hasBackground}
            section={section}
            onClick={executeCallBack}
        >
            {linkLabel}
        </StyledInternalLink>
    );
};

/* --------------------------------- Export --------------------------------- */

export default DumbLinkAtom;
